import { useCallback } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';

import { QuestionConfigBasicInformation } from './QuestionConfigBasicInformation';
import { QuestionConfigDocumentation } from './QuestionConfigDocumentation';
import { buildPatchConfig } from './questionConfigShared';
import type { QuestionConfigFieldErrors } from '../../../shared/domain/questionConfigValidation';
import type { ReviewQuestion, ReviewQuestionConfiguration } from '../logic/reviewTypes';

export interface QuestionConfigurationStepProps {
  /** The question under review whose configuration this step edits. */
  readonly question: ReviewQuestion;
  /** Emits a new question with the edited configuration. Parent owns state. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  /** Per-field inline-error flags (from `questionConfigFieldErrors`), threaded
   *  into the Basic Information card. Defaults to all-false. */
  readonly fieldErrors?: QuestionConfigFieldErrors;
  /** Disables every field (e.g. once the question is published). */
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * Third review wizard step: the editable "Question Configuration" form. It
 * combines the manual question flow's Basic Information + Documentation steps
 * into two peer cards — overview, division, category, audit score, tags,
 * excluded locations, the four Comment/Attachment requirement toggles, the
 * read-only Business Unit and Source Policy ID, and the supporting
 * documentation links.
 *
 * Composes the shared form primitives (`OptionSelect`, `OptionMultiSelect`,
 * `AuditScoreField`, `ChipTokenGroup`, `DocLinksField`,
 * `ReviewQuestionToggleField`) through the two card sub-components so the same
 * field groups can later replace the Cloudscape controls in the manual flow.
 *
 * DATA WIRING DEFERRED: the Division / Category / Tag option universes come
 * from the draft's own current selections for now — a follow-up CR wires the
 * real Orbit/Helios option sources. Fully controlled: patches
 * `question.configuration` and emits the new draft on every edit.
 */
export function QuestionConfigurationStep({
  question,
  onQuestionChange,
  fieldErrors,
  disabled = false,
  dataTestId,
}: QuestionConfigurationStepProps): JSX.Element {
  const config = question.configuration;

  const patchConfig = useCallback(
    (patch: Partial<ReviewQuestionConfiguration>) =>
      buildPatchConfig(question, onQuestionChange)(patch),
    [question, onQuestionChange],
  );

  return (
    <Col gridGap="dimensions.spacing.400" width="100%">
      <QuestionConfigBasicInformation
        config={config}
        patchConfig={patchConfig}
        disabled={disabled}
        {...(fieldErrors !== undefined ? { fieldErrors } : {})}
        {...(dataTestId !== undefined ? { dataTestId } : {})}
      />
      <QuestionConfigDocumentation
        config={config}
        patchConfig={patchConfig}
        disabled={disabled}
        {...(dataTestId !== undefined ? { dataTestId } : {})}
      />
    </Col>
  );
}
